import React, { useEffect, useState } from 'react';
import { X, FileText } from 'lucide-react';
import './ViewMotivationLetterModal.scss';

interface CoverLetter {
  id: number;
  user_id: number;
  title: string;
  content: string;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

interface ViewMotivationLetterModalProps {
  userId: number;
  onClose: () => void;
}

/**
 * ViewMotivationLetterModal Component
 * 
 * Modal for viewing a candidate's primary (preferred) motivation letter.
 * Displays the cover letter content that the candidate has marked as primary.
 */
const ViewMotivationLetterModal: React.FC<ViewMotivationLetterModalProps> = ({
  userId,
  onClose
}) => {
  const [coverLetter, setCoverLetter] = useState<CoverLetter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPrimaryCoverLetter = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      // Fetch all cover letters for this user
      const response = await fetch(`${API_URL}/cover-letters/user/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        const coverLetters = result.data || result || [];
        
        // Find the primary cover letter
        const primary = coverLetters.find((cl: CoverLetter) => cl.is_primary);
        
        if (primary) {
          setCoverLetter(primary);
        } else if (coverLetters.length > 0) {
          // If no primary, show the first one
          setCoverLetter(coverLetters[0]);
        } else {
          setError('Kandidatas dar neturi įkėlęs motyvacinį laišką');
        }
      } else {
        setError('Nepavyko užkrauti motyvacinio laiško');
      }
    } catch (error) {
      console.error('Error fetching cover letter:', error);
      setError('Įvyko klaida kraunant motyvacinį laišką');
    } finally {
      setLoading(false);
    }
    };

    fetchPrimaryCoverLetter();
  }, [userId]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="motivation-letter-modal" onClick={(e) => e.stopPropagation()}>
        <button className="btn-close-modal" onClick={onClose}>
          <X size={24} />
        </button>

        <div className="modal-header">
          <FileText size={32} />
          <h2 className="modal-title">Motyvacinis laiškas</h2>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Kraunamas motyvacinis laiškas...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <FileText size={48} />
              <p>{error}</p>
            </div>
          ) : coverLetter ? (
            <div className="letter-content">
              {coverLetter.content}
            </div>
          ) : (
            <div className="empty-state">
              <FileText size={48} />
              <p>Motyvacinis laiškas nerastas</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-close" onClick={onClose}>
            Uždaryti
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewMotivationLetterModal;

